import { type FC, type KeyboardEvent } from 'react'
import classNames from 'classnames'
import { Dropdown, type IDropdownItem } from 'shared/ui/Dropdown'
import { Icon } from 'shared/ui/Icon'
import { Typography } from 'shared/ui/Typography'
import { Error } from 'shared/ui/Error'
import { type ITooltipProps } from 'shared/ui/Tooltip'
import styles from './styles.module.scss'
import {
  type IInputComponent,
  type IInputPatternProps,
  makeInputPattern,
  makeTextFieldElement,
  makeTextFieldHighlight,
  makeTextFieldIcon,
  TextField,
  TextFieldPlaceholder,
} from '../TextField'
import { Label } from '../Label'
import { CountryEnum } from './types'

const generateDynamicFormat = (value: string) => {
  const cleanedValue = value?.replace(/\D/g, '')
  return cleanedValue?.length >= 7 ? '(###) ###-####' : '(###) ### ####'
}

const InputPhoneDefault = makeInputPattern({
  format: '(###) ###-####',
  placeholder: '(555) 800-8000',
  generateDynamicFormat,
})

const isValidCountryEnum = (id: string | number): id is CountryEnum => {
  if (typeof id !== 'string') return false

  const validCountries: string[] = Object.values(CountryEnum)

  return validCountries.includes(id)
}

const InputPhoneByCountry: Record<CountryEnum, IInputComponent<Partial<IInputPatternProps>>> = {
  [CountryEnum.GB]: makeInputPattern({
    format: '#### ######',
    placeholder: '5555 800800',
  }),

  [CountryEnum.AU]: makeInputPattern({
    format: '### ### ###',
    placeholder: '412 345 678',
  }),

  [CountryEnum.US]: InputPhoneDefault,

  [CountryEnum.CA]: InputPhoneDefault,

  [CountryEnum.World]: InputPhoneDefault,
}

type IPhoneWithLabelInputProps = {
  items: IDropdownItem[]
  number: string
  loading?: boolean
  numberError?: string | null
  disabled?: boolean
  hideError?: boolean
  item: IDropdownItem
  numberRequestError?: string | null
  isValidNumber?: boolean
  label?: string
  questionTooltipProps?: ITooltipProps
  triggerNumberValidation?: () => void
  setNumber?: (value: string) => void
  setCountry?: (item: IDropdownItem, child?: IDropdownItem) => void
  hideLabel?: boolean
  readOnly?: boolean
  isNoMarginTopInputLabel?: boolean
}

export const PhoneWithLabelInput: FC<IPhoneWithLabelInputProps> = ({
  items,
  numberError,
  numberRequestError,
  number,
  loading = false,
  item,
  label = 'Mobile phone number',
  isValidNumber,
  questionTooltipProps,
  setCountry,
  setNumber,
  triggerNumberValidation,
  hideLabel = false,
  hideError = false,
  disabled = false,
  readOnly = false,
  isNoMarginTopInputLabel,
}) => {
  const onKeyDownNumber = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerNumberValidation?.()
      if (isValidNumber && document.activeElement instanceof HTMLElement)
        document.activeElement.blur()
    }
  }
  return (
    <>
      {!hideLabel && (
        <Label
          label={{
            text: label,
            className: classNames(styles.inputLabel, {
              [styles.noMarginTop]: isNoMarginTopInputLabel,
            }),
          }}
          questionTooltipProps={questionTooltipProps}
        />
      )}
      <TextField
        className={classNames(styles.textField, {
          [styles.error]: (numberError || numberRequestError) && !hideError,
        })}
        Input={isValidCountryEnum(item.id) ? InputPhoneByCountry[item.id] : InputPhoneDefault}
        InputProps={{ onKeyDown: onKeyDownNumber }}
        error={!!numberRequestError}
        variant='stroke'
        size='medium'
        value={number}
        disabled={loading || disabled}
        onChange={setNumber}
        onBlur={triggerNumberValidation}
        readOnly={readOnly}
        leftActions={[
          makeTextFieldElement({
            element: (
              <div className={styles.leftContentNumber}>
                <Dropdown
                  placement={'bottom-start'}
                  width={191}
                  margin={-28}
                  marginLeft={-12}
                  disabled={disabled || readOnly}
                  items={items}
                  onChange={setCountry}
                  triggerComponent={() => (
                    <TextFieldPlaceholder
                      rightActions={[
                        makeTextFieldIcon({
                          icon: 'chevronDown',
                        }),
                      ]}
                      disabled={loading || disabled}
                      placeholder={<Icon icon={item.iconL} />}
                      variant={'integrated'}
                      size={'small'}
                      className={styles.countryPicker}
                    />
                  )}
                />
                <div className={styles.verticalDivider}></div>
                <Typography variant={'text-input-field'} ariaLabel={'numberPrefix'}>
                  {item.labelRight}
                </Typography>
              </div>
            ),
          }),
        ]}
        mainActions={[makeTextFieldHighlight({ mode: 'error', highlight: !!numberError })]}
      />
      {!hideError && <Error type={'text'} error={numberError || numberRequestError} />}
    </>
  )
}
